import React from 'react';
import { Card, CardHeader, CardBody, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from "@heroui/react";
import { Calendar } from "@phosphor-icons/react";
import { ContactType, MilestoneDateType, RentalContract } from "shared/types/rental-contracts";
import { formatDate } from "shared/utils/get-dates";
import { useNavigate } from "react-router-dom";
import ColoredDot from 'screens/buildings/components/operationalDashboard/components/coloredDot';

interface ContractsThisYearProps {
    contracts: RentalContract[];
}

const ContractsThisYear: React.FC<ContractsThisYearProps> = ({ contracts }) => {
    const navigate = useNavigate();

    const evaluateContract = (contract: RentalContract) => {
        const operationalChecks: any = {
            green: { count: 0, labels: [] },
            orange: { count: 0, labels: [] },
            red: { count: 0, labels: [] },
            transparent: { count: 0, labels: [] },
        };

        // Check guarantee
        const hasGuarantee = contract?.guarantee?.length === undefined || contract?.guarantee?.length === 0;
        if (hasGuarantee) {
            operationalChecks.green.count++;
            operationalChecks.green.labels.push("Huurgarantie aanwezig");
        } else {
            const guaranteeStatus = contract.documentStatuses?.find(
                status => status.documentType === 'Huurwaarborg'
            );
            if (guaranteeStatus) {
                if (guaranteeStatus.status === 'MISSING_NOT_REQUIRED') {
                    operationalChecks.green.count++;
                    operationalChecks.green.labels.push(`Huurwaarborg niet vereist: ${guaranteeStatus.reason}`);
                } else {
                    operationalChecks.red.count++;
                    operationalChecks.red.labels.push(`Huurwaarborg vereist maar niet beschikbaar: ${guaranteeStatus.reason}`);
                }
            } else {
                operationalChecks.red.count++;
                operationalChecks.red.labels.push("Huurgarantie niet aanwezig");
            }
        }

        // Check required documents
        const requiredFileLabels = [
            "Ingaande plaatsbeschrijving",
            "Registratiebewijs huurcontract",
            "Huurcontract",
            "Brandverzekering huurder"
        ];

        requiredFileLabels.forEach(label => {
            const documentExists = contract.fileLogs?.some(file => file && file.label?.includes(label));
            const documentStatus = contract.documentStatuses?.find(
                status => status.documentType === label
            );

            if (documentExists) {
                operationalChecks.green.count++;
                operationalChecks.green.labels.push(`${label} aanwezig`);
            } else if (documentStatus) {
                if (documentStatus.status === 'MISSING_NOT_REQUIRED') {
                    operationalChecks.green.count++;
                    operationalChecks.green.labels.push(`${label} niet vereist: ${documentStatus.reason}`);
                } else {
                    operationalChecks.red.count++;
                    operationalChecks.red.labels.push(`${label} vereist maar niet beschikbaar: ${documentStatus.reason}`);
                }
            } else {
                operationalChecks.red.count++;
                operationalChecks.red.labels.push(`${label} niet aanwezig`);
            }
        });

        return operationalChecks;
    };

    const handleRowClick = () => {
        navigate('/rental', { state: { scrollTo: 'missing-docs' } });
    };

    return (
        <Card className="bg-white shadow-sm">
            <CardHeader className="border-b">
                <h3 className="text-lg font-medium flex items-center gap-2">
                    <Calendar weight="duotone" size={20} />
                    Afgesloten contracten dit jaar
                </h3>
            </CardHeader>
            <CardBody>
                <Table aria-label="Contracts created this year">
                    <TableHeader>
                        <TableColumn>Aangemaakt op</TableColumn>
                        <TableColumn>Unit</TableColumn>
                        <TableColumn>Status</TableColumn>
                        <TableColumn>Huurder(s)</TableColumn>
                        <TableColumn>Startdatum</TableColumn>
                        <TableColumn>Type contract</TableColumn>
                        <TableColumn>Status</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {contracts?.map((contract: RentalContract) => {
                            const startDate = contract.contractMilestoneDates
                                ?.find(date => date.milestoneDateType === MilestoneDateType.START_DATE)
                                ?.date;

                            const operationalChecks = evaluateContract(contract);

                            return (
                                <TableRow
                                    key={contract.id}
                                    className="cursor-pointer"
                                    onClick={handleRowClick}
                                >
                                    <TableCell>
                                        {contract.createdAt ? formatDate(new Date(contract.createdAt)) : "N/A"}
                                    </TableCell>
                                    <TableCell>{contract.unit?.id}</TableCell>
                                    <TableCell>
                                        <div className="flex cursor-pointer items-center justify-center rounded-lg">
                                            <ColoredDot
                                                color="green"
                                                number={operationalChecks.green.count}
                                                labels={operationalChecks.green.labels}
                                            />
                                            <ColoredDot
                                                color="orange"
                                                number={operationalChecks.orange.count}
                                                labels={operationalChecks.orange.labels}
                                            />
                                            <ColoredDot
                                                color="red"
                                                number={operationalChecks.red.count}
                                                labels={operationalChecks.red.labels}
                                            />
                                            <ColoredDot
                                                color="transparent"
                                                number={operationalChecks.transparent.count}
                                                labels={operationalChecks.transparent.labels}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {contract.contractContacts
                                            ?.filter(contact => contact.contactType === ContactType.TENANT)
                                            .map(contact => contact.resource?.name)
                                            .join(", ")}
                                    </TableCell>
                                    <TableCell>
                                        {startDate ? formatDate(new Date(startDate)) : "N/A"}
                                    </TableCell>
                                    <TableCell>{contract.legalForm}</TableCell>
                                    <TableCell>{contract.phase}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardBody>
        </Card>
    );
};

export default ContractsThisYear; 